import { usePrevious } from '@sequencehq/utils'
import React, { useEffect, useState } from 'react'
import { BulkActionsPositioner, ScrollFade } from '@sequencehq/core-components'
import { useEnrichedBulkActions } from './hooks/useEnrichedBulkActions'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useMarkCompletedBulkActionsAsReviewed } from './hooks/useMarkCompletedBulkActionsAsReviewed'
import { BulkActionInFlight } from './BulkActionInFlight'
import { BulkAction, BulkActionEntityConfig, BulkActionType } from './types'

const INACTIVE_REFETCH_INTERVAL = 30000
const ACTIVE_REFETCH_INTERVAL = 2000

type Props<TEntity> = {
  selectionCount?: number
  onChangeActiveBulkActionCount?: (numberOfBulkActions: number) => void
  onChangeHasBulkActions?: (hasBulkActions: boolean) => void
  /**
   * Describes how the entity (invoices, quotes, …) enriches and renders. The
   * bulk-action types it owns — and which this panel therefore monitors — are
   * the keys of `config.titles`.
   */
  config: BulkActionEntityConfig<TEntity>
}

/**
 * When we load into the module, we don't want to display previously completed
 * that are successful. It is particularly important when releasing this feature,
 * or using the bulk action api directly, as we will have large numbers of unreviewed
 * bulk actions.
 *
 * Unsuccessful bulk actions will still be shown!
 *
 * This means that this is actually quite a fiddly lil' beastie. It needs to:
 * - Only trigger once, once the bulk actions have loaded in for the first time.
 * - Don't cause a 'flicker' of bulk actions that are about to be dismissed
 * - Don't trigger for successful bulk actions that happen whilst the component is mounted.
 *
 * The latter is pretty much impossible to nail, well, since in theory a bulk action may
 * instantly resolve successfully so the first poll result is success. It would require
 * this component to be pre-mounted in the callsite. Which, in itself, is reasonable but can
 * be easily forgotten in novel use cases. It'll be a smidge easier if/when we move this to
 * be a global module that is ever present.
 *
 * Pragmatically, though, that scenario is incredibly unlikely.
 *
 * @param props
 */
const useAutomaticallyReviewSuccessfullyCompletedBulkActionsOnLoad = (props: {
  bulkActionsLoading: boolean
  bulkActions: Array<{
    id: string
    type: BulkAction['type']
    status: BulkAction['status']
    reviewedAt?: BulkAction['reviewedAt']
  }>
  /** Download types (from the entity config) auto-dismiss on any terminal state. */
  downloadTypes: BulkActionType[]
}): {
  automaticallyReviewedBulkActionIds: string[]
} => {
  /**
   * We only want to run this once per mount. Once the bulk actions have loaded for the
   * first time, lock things out from running again until a remount.
   */
  const reviewBulkAction = useMutation(
    dashboard20240730Client.postReviewBulkAction
  )
  const [autoCompleted, setAutoCompleted] = useState(false)

  const bulkActionsToAutoComplete = !autoCompleted
    ? (props.bulkActions?.filter(ba => {
        if (ba.reviewedAt) {
          return false
        }
        // A download's only output is the zip, fetched and saved client-side
        // and never persisted — once it is terminal there is nothing to
        // review. Dismiss old downloads regardless of partial failures, or a
        // COMPLETED_WITH_FAILURES download lingers and re-shows on every load.
        // Other actions still only auto-dismiss on full success, so genuine
        // failures stay in the panel for the user to review.
        return props.downloadTypes.includes(ba.type)
          ? ba.status !== 'PROCESSING'
          : ba.status === 'COMPLETED'
      }) ?? [])
    : []

  useEffect(() => {
    if (
      autoCompleted ||
      reviewBulkAction.isPending ||
      props.bulkActionsLoading
    ) {
      return
    }
    // On the first settled load, dismiss the actions that were already complete
    // when we arrived so old successes don't clutter the panel, then lock.
    //
    // Locking here even when there is nothing to dismiss is the important part:
    // otherwise `autoCompleted` never flips on an account with no completed
    // actions at load, and this effect stays armed to dismiss the very next
    // action that completes while mounted — e.g. the user's first download,
    // which would flash into the panel and vanish before they can grab the zip.
    //
    // Auto-review is best-effort cleanup; a failed call just leaves the stale
    // action in the panel. Swallow the error and lock regardless, so the
    // effect doesn't re-fire the same failing review on every render/poll.
    Promise.all(
      bulkActionsToAutoComplete.map(bulkAction =>
        reviewBulkAction.mutateAsync({ id: bulkAction.id })
      )
    )
      .catch(() => {})
      .finally(() => {
        setAutoCompleted(true)
      })
  }, [
    bulkActionsToAutoComplete,
    autoCompleted,
    props.bulkActionsLoading,
    reviewBulkAction
  ])

  return {
    automaticallyReviewedBulkActionIds: bulkActionsToAutoComplete.map(
      ba => ba.id
    )
  }
}

const useBulkActionsInFlight = <TEntity,>(props: {
  selectionCount: number
  config: BulkActionEntityConfig<TEntity>
}) => {
  const [reviewingId, setReviewingId] = useState<string | null>(null)
  const [refetchInterval, setRefetchInterval] = useState(
    INACTIVE_REFETCH_INTERVAL
  )

  // The types this entity owns are exactly the keys of its title config.
  const bulkActionTypes = React.useMemo(
    () => Object.keys(props.config.titles) as BulkActionType[],
    [props.config.titles]
  )

  const enrichedBulkActions = useEnrichedBulkActions({
    refetchInterval,
    types: bulkActionTypes,
    enrich: props.config.enrich
  })

  const hasActiveBulkActions = enrichedBulkActions.bulkActions.some(
    bulkAction => bulkAction.status === 'PROCESSING'
  )
  const prevHasActiveBulkActions = usePrevious(hasActiveBulkActions)

  useEffect(() => {
    if (prevHasActiveBulkActions === hasActiveBulkActions) {
      return
    }
    setRefetchInterval(
      hasActiveBulkActions ? ACTIVE_REFETCH_INTERVAL : INACTIVE_REFETCH_INTERVAL
    )
  }, [hasActiveBulkActions, prevHasActiveBulkActions])

  const {
    automaticallyReviewedBulkActionIds: automaticallyReviewedBulkActions
  } = useAutomaticallyReviewSuccessfullyCompletedBulkActionsOnLoad({
    bulkActions: enrichedBulkActions.bulkActions,
    bulkActionsLoading: enrichedBulkActions.isLoading,
    downloadTypes: props.config.downloadZip?.types ?? []
  })

  // Memoize filtered bulk actions
  const enrichedBulkActionsWithoutAutoCompletedActions = React.useMemo(
    () => ({
      ...enrichedBulkActions,
      bulkActions: enrichedBulkActions.bulkActions.filter(
        ba => !automaticallyReviewedBulkActions.includes(ba.id)
      )
    }),
    [enrichedBulkActions, automaticallyReviewedBulkActions]
  )

  const reviewBulkAction = useMutation(
    dashboard20240730Client.postReviewBulkAction
  )

  const cancelBulkAction = useMutation(
    dashboard20240730Client.postCancelBulkAction
  )

  const cancel = React.useCallback(
    (id: string) => {
      cancelBulkAction.mutate({ id })
    },
    [cancelBulkAction]
  )

  const dismiss = React.useCallback(
    (id: string) => {
      reviewBulkAction.mutate({ id })
      setReviewingId(prev => (prev === id ? null : prev))
    },
    [reviewBulkAction]
  )

  const markCompletedBulkActionsAsReviewed =
    useMarkCompletedBulkActionsAsReviewed(bulkActionTypes)
  const previousSelectionCount = usePrevious(props.selectionCount)

  useEffect(() => {
    if (!props.selectionCount) return

    if (
      previousSelectionCount !== props.selectionCount &&
      props.selectionCount > 0
    ) {
      markCompletedBulkActionsAsReviewed()
    }
  }, [
    props.selectionCount,
    previousSelectionCount,
    markCompletedBulkActionsAsReviewed
  ])

  return {
    enrichedBulkActions: enrichedBulkActionsWithoutAutoCompletedActions,
    dismiss,
    cancel,
    isCancelling: cancelBulkAction.isPending,
    reviewingId,
    setReviewingId
  }
}

export const BulkActionsInFlight = <TEntity,>({
  selectionCount = 0,
  onChangeActiveBulkActionCount,
  onChangeHasBulkActions,
  config
}: Props<TEntity>) => {
  const {
    enrichedBulkActions,
    dismiss,
    cancel,
    isCancelling,
    reviewingId,
    setReviewingId
  } = useBulkActionsInFlight({
    selectionCount,
    config
  })

  const activeBulkActionsCount = enrichedBulkActions.bulkActions.filter(
    ba => ba.status === 'PROCESSING'
  ).length

  const prevActiveBulkActionsCount = usePrevious(activeBulkActionsCount)
  useEffect(() => {
    if (prevActiveBulkActionsCount === activeBulkActionsCount) {
      return
    }
    onChangeActiveBulkActionCount?.(activeBulkActionsCount)
  }, [
    activeBulkActionsCount,
    onChangeActiveBulkActionCount,
    prevActiveBulkActionsCount
  ])

  // Show the panel as soon as the bulk actions load. The summary (title +
  // counts) needs no enrichment; enrichment only feeds the Review drill-down,
  // and renderRow falls back gracefully until it lands. Gating on enrichment
  // here just held the panel behind an extra round trip.
  const hasBulkActions = enrichedBulkActions.bulkActions.length > 0

  const prevHasBulkActions = usePrevious(hasBulkActions)
  useEffect(() => {
    if (prevHasBulkActions === hasBulkActions) {
      return
    }
    onChangeHasBulkActions?.(hasBulkActions)
  }, [hasBulkActions, onChangeHasBulkActions, prevHasBulkActions])

  if (!hasBulkActions) {
    return null
  }

  return (
    <BulkActionsPositioner>
      <ScrollFade maxHeight={400}>
        {enrichedBulkActions.bulkActions.map(bulkAction => {
          return (
            <BulkActionInFlight
              key={bulkAction.id}
              bulkAction={bulkAction}
              onDismiss={() => dismiss(bulkAction.id)}
              entities={enrichedBulkActions.entities}
              config={config}
              isReviewing={reviewingId === bulkAction.id}
              onChangeReviewing={isReviewing =>
                setReviewingId(isReviewing ? bulkAction.id : null)
              }
              onCancel={cancel}
              isCancelling={isCancelling}
            />
          )
        })}
      </ScrollFade>
    </BulkActionsPositioner>
  )
}
